import { lazy, useEffect, useState } from "react";

import { MonitorX, PenLine } from "lucide-react";
import { useNavigate } from "react-router";

import { buildSessionUrl } from "@/components/pages_logged_in/roleplays/utils";
import { IconCircle } from "@/components/pieces/IconCircle";
import { DifficultyTag } from "@/components/pieces/roleplays/DifficultyTag";
import {
  CallStatus,
  LiveCallRenderProps,
} from "@/components/pieces/roleplays/LiveCallManager";
import { AvatarWithVolume } from "@/components/pieces/users/AvatarWithVolume";
import { Button } from "@/components/ui/button";
import {
  PDFViewerWithControls,
  SLIDE_FOOTER_HEIGHT,
} from "@/components/ui/pdf-viewer";
import { CARD_CLASSES, CTA_COLOR, LIGHT_BLUE } from "@/css/constants";
import {
  CurrentSubscriptionDocument,
  useCreateRoleplaySessionMutation,
  useWorkspaceNavQuery,
} from "@/graphql";
import { useContainerDimensions } from "@/hooks/use-container-dimensions";
import { useDebouncedCallback } from "@/hooks/use-debounced-callback";
import { FullScenario, Presentation } from "@/types/roleplays";
import { ensureNonNullArray } from "@/utils/array";

import { AvatarBox } from "./AvatarBox";
import {
  ChatAvatarPresentationAnimation,
  MODERN_SLIDE_ASPECT_RATIO,
} from "./ChatAvatarPresentationAnimation";
import { ShowOrStopButton } from "./PresentationsPicker";
import { ScenarioCallControls } from "./ScenarioCallControls";
import { useScreenShare } from "./ScreenShare";

const LiveCallManager = lazy(
  () => import("@/components/pieces/roleplays/LiveCallManager"),
);

interface PlayScenarioCardProps {
  scenario: FullScenario;
  roleplaySlug?: string;
  onEditCharacter: () => void;
  onEditScenario: () => void;
}

export function PlayScenarioCard({
  scenario,
  roleplaySlug,
  onEditCharacter,
  onEditScenario,
}: PlayScenarioCardProps) {
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [selectedPresentation, setSelectedPresentation] =
    useState<Presentation | null>(null);
  const evaluationCriteria = ensureNonNullArray(scenario.evaluationCriteria);
  const navigate = useNavigate();
  const [saveError, setSaveError] = useState("");
  const { data } = useWorkspaceNavQuery();

  const [saveCall] = useCreateRoleplaySessionMutation({
    onCompleted: (data) => {
      const roleplaySessionUuid =
        data.createRoleplaySession?.roleplaySession?.uuid;
      if (roleplaySessionUuid) {
        navigate(
          buildSessionUrl({
            roleplaySlug,
            scenarioSlug: scenario.slug,
            sessionUuid: roleplaySessionUuid,
          }),
        );
      }
    },
    onError: (error) => {
      setSaveError(error.message);
    },
    refetchQueries: [{ query: CurrentSubscriptionDocument }],
  });

  function handleSaveCall(callId?: string) {
    if (!callId) return;

    saveCall({
      variables: {
        scenarioId: scenario.id,
        callId,
        roleplaySlug: roleplaySlug,
      },
    });
  }

  return (
    <LiveCallManager
      assistantId={scenario.assistantId ?? ""}
      onVolumeChange={setVolumeLevel}
      handleSaveCall={handleSaveCall}
      saveError={saveError}
      introductionAudioUrl={scenario.introductionAudioUrl ?? undefined}
      disabled={
        evaluationCriteria?.length === 0 ||
        evaluationCriteria?.every((criterion) => criterion.items?.length === 0)
      }
      variableValues={{
        first_name: scenario?.personaFirstName ?? "",
        last_name: scenario?.personaLastName ?? "",
      }}
      onCallRestarted={() => setSelectedPresentation(null)}
    >
      {(props) => (
        <PlayScenarioCardView
          callStatus={props.callState.status}
          scenario={scenario}
          onEditCharacter={onEditCharacter}
          onEditScenario={onEditScenario}
          liveCallRenderProps={props}
          volumeLevel={volumeLevel}
          selectedPresentation={selectedPresentation}
          setSelectedPresentation={setSelectedPresentation}
          data={data}
          enableScreenSharing={scenario.enableScreenSharing}
        />
      )}
    </LiveCallManager>
  );
}

export function PlayScenarioCardView(props: {
  callStatus: CallStatus;
  onEditCharacter: () => void;
  onEditScenario: () => void;
  scenario: FullScenario;
  liveCallRenderProps: LiveCallRenderProps;
  volumeLevel: number;
  selectedPresentation: Presentation | null;
  setSelectedPresentation: (presentation: Presentation | null) => void;
  data:
    | { currentUser?: { name?: string | null; photo?: string | null } }
    | undefined;
  playIntroductionAudioOnRender?: boolean;
  enableScreenSharing: boolean;
}) {
  useEffect(() => {
    if (props.callStatus === CallStatus.IDLE) {
      setSelectedPresentation(null);
    }
  }, [props.callStatus]);
  const {
    callStatus,
    scenario,
    onEditCharacter,
    onEditScenario,
    liveCallRenderProps,
    volumeLevel,
    selectedPresentation,
    setSelectedPresentation,
    data = {},
    playIntroductionAudioOnRender,
  } = props;
  switch (callStatus) {
    case CallStatus.IDLE:
    case CallStatus.STARTING:
      return (
        <ScenarioCardInnerUnconnected
          callStatus={callStatus}
          scenario={scenario}
          onEditCharacter={onEditCharacter}
          onEditScenario={onEditScenario}
          liveCallRenderProps={liveCallRenderProps}
          volumeLevel={volumeLevel}
          playIntroductionAudioOnRender={playIntroductionAudioOnRender}
        />
      );

    default:
      return (
        <ScenarioCardInnerConnected
          callStatus={callStatus}
          scenario={scenario}
          onEditCharacter={onEditCharacter}
          liveCallRenderProps={liveCallRenderProps}
          volumeLevel={volumeLevel}
          selectedPresentation={selectedPresentation}
          onPresentationChange={setSelectedPresentation}
          userName={data?.currentUser?.name}
          userPhoto={data?.currentUser?.photo}
          enableScreenSharing={props.enableScreenSharing}
        />
      );
  }
}

function ScenarioCardInnerUnconnected(props: {
  callStatus: CallStatus.IDLE | CallStatus.STARTING;
  scenario: FullScenario;
  onEditCharacter: () => void;
  onEditScenario: () => void;
  liveCallRenderProps: LiveCallRenderProps;
  volumeLevel: number;
  playIntroductionAudioOnRender?: boolean;
}) {
  const personaByLine = [
    props.scenario.personaJobTitle,
    props.scenario.personaCompany,
  ]
    .filter(Boolean)
    .join(" @ ");

  return (
    <div
      className={`grid auto-rows-auto sm:grid-cols-[auto_1fr] items-center bg-white gap-9 p-6 ${CARD_CLASSES}`}
    >
      <div className="relative grid row-1 col-1 w-full sm:w-68 content-center gap-4 grid-flow-row justify-center h-full bg-gray-100 p-6 rounded-md">
        <AvatarWithVolume
          user={{
            name: props.scenario.personaName ?? "",
            photo: props.scenario.personaPhotoUrl ?? "",
          }}
          size={100}
          volumeLevel={props.volumeLevel}
        />
        <div className="grid text-center items-center content-center justify-center justify-items-center">
          <div className="mb-1 line-clamp-3 font-semibold">
            {props.scenario.personaName}
          </div>
          {personaByLine && (
            <div className="text-center line-clamp-3 text-gray-700">
              {personaByLine}
            </div>
          )}
          <div className="mt-4 grid justify-center">
            <DifficultyTag difficulty={props.scenario.difficulty} />
          </div>
        </div>
        {props.scenario.canEdit && (
          <div className="absolute top-2 right-2">
            <Button
              variant="minimal"
              intent="secondary"
              onClick={props.onEditCharacter}
              size="icon"
            >
              <PenLine />
            </Button>
          </div>
        )}
      </div>
      <div className="grid h-full sm:col-2 sm:row-1">
        <div className="grid content-center gap-8">
          <div className="grid gap-2">
            <div className="self-stretch inline-flex justify-start items-center gap-2.5">
              <IconCircle
                icon="endorsed"
                size={30}
                backgroundColor={LIGHT_BLUE}
                color={CTA_COLOR}
              />
              <div className="justify-start text-gray-800 text-lg font-semibold leading-relaxed">
                Your Objective
              </div>
            </div>
            <div className="text-left max-w-2xl self-stretch justify-start text-gray-800 text-base font-normal leading-snug">
              {props.scenario.objective?.split("\n")[0]}
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <div className="grid justify-start">
              <ScenarioCallControls
                scenarioId={props.scenario.id}
                liveCallRenderProps={props.liveCallRenderProps}
                callStatus={props.callStatus}
                playIntroductionAudioOnRender={
                  props.playIntroductionAudioOnRender
                }
              />
            </div>
            <div className="text-sm text-gray-500">
              Best with headphones & quiet space.
              {props.scenario.autoShareSessions
                ? ` Your sessions will be visible to others.`
                : null}
            </div>
          </div>
        </div>
      </div>
      {props.scenario.canEdit && (
        <div className="col-start-1 row-start-1 sm:col-start-2 sm:row-start-1 justify-self-end self-start sm:pr-0">
          <Button
            variant="minimal"
            intent="secondary"
            onClick={props.onEditScenario}
            size="icon"
          >
            <PenLine />
          </Button>
        </div>
      )}
    </div>
  );
}

function ScenarioCardInnerConnected(props: {
  callStatus:
    | CallStatus.CONNECTING
    | CallStatus.RESTARTING
    | CallStatus.CONNECTED
    | CallStatus.ENDING
    | CallStatus.ERROR
    | CallStatus.SAVE_FAILED;
  scenario: FullScenario;
  onEditCharacter: () => void;
  liveCallRenderProps: LiveCallRenderProps;
  volumeLevel: number;
  selectedPresentation: Presentation | null;
  onPresentationChange: (presentation: Presentation | null) => void;
  enableScreenSharing: boolean;
  userName?: string | null;
  userPhoto?: string | null;
}) {
  const [
    /** This is stored so that the exit animation of the slide would not be scaling down an empty view. */
    lastPresentationUrl,
    setLastPresentationUrl,
  ] = useState<string | null>(null);

  useEffect(() => {
    if (props.selectedPresentation?.file.url) {
      setLastPresentationUrl(props.selectedPresentation.file.url);
    }
  }, [props.selectedPresentation?.file.url]);

  const screenShare = useScreenShare({
    containerAspectRatio: MODERN_SLIDE_ASPECT_RATIO,
    onImageAnalzyed: (result) => {
      const message =
        `[Context Update: The user is sharing screen. Treat this information as if it was seen visually and in character.]` +
        `<screen-description>${result.description}</screen-description>`;
      props.liveCallRenderProps.sendBackgroundMessage(message);
    },
  });

  useEffect(() => {
    const message = screenShare.active
      ? // Screen share active
        `[Context update:
      The user started screen sharing, if you have not received any screen descriptions yet,
      and the user asked if you can see the screen,
      always confirm that you can see their screen
      and indicate that you're actively reviewing the content.
      ]`
      : // Screen share NOT active
        `[Context update: the user stopped sharing their screen.]`;

    props.liveCallRenderProps.sendBackgroundMessage(message);
  }, [screenShare.active]);

  const presentationHeader = useContainerDimensions();

  const isPresenting =
    screenShare.active || props.selectedPresentation !== null;

  const onPageChange = useDebouncedCallback((pageIndex: number) => {
    const description = props.selectedPresentation?.pageDescriptions[pageIndex];
    if (description) {
      const filename = props.selectedPresentation?.file.name;
      const message =
        `[Context Update: The user has navigated to slide ${pageIndex + 1} of "${filename}". Treat this information as if it was seen visually and in character.]` +
        `<slide-description>${description}</slide-description>`;
      if (!import.meta.env.PROD) {
        console.log(message);
      }
      props.liveCallRenderProps.sendBackgroundMessage(message);
    }
  }, 1000);

  const videoView = screenShare.Video ? (
    <div className="grid h-full w-full grid-rows-[auto_1fr]">
      <div
        ref={presentationHeader.setContainerRef}
        className="grid w-full grid-cols-[1fr_auto] items-center py-3 px-7"
      >
        <div className="hidden sm:grid justify-start truncate text-start text-white text-sm font-semibold leading-tight">
          You are sharing your screen
        </div>
        <ShowOrStopButton
          intent="primary"
          variant="glass"
          icon={MonitorX}
          text="Stop Screen Sharing"
          onClick={() => screenShare.stop()}
        />
      </div>
      {screenShare.Video()}
    </div>
  ) : null;

  return (
    <div className="grid items-center bg-white text-center gap-2 p-2 border border-gray-300 rounded-md">
      <ChatAvatarPresentationAnimation
        transitionDuration={300}
        showPresentation={isPresenting}
        presentationHeaderHeight={presentationHeader.dimensions.height}
        presentationFooterHeight={
          screenShare.active ? 0 : lastPresentationUrl ? SLIDE_FOOTER_HEIGHT : 0
        }
        botAvatar={
          <AvatarBox
            name={props.scenario.personaName}
            photo={props.scenario.personaPhotoUrl}
            backgroundColor="#F5F6F8"
            volumeLevel={props.volumeLevel}
            isPresenting={isPresenting}
          />
        }
        userAvatar={
          <AvatarBox
            name={props.userName}
            photo={props.userPhoto}
            backgroundColor="#242424"
            volumeLevel={0}
            isPresenting={isPresenting}
          />
        }
        presentation={
          videoView ??
          (lastPresentationUrl ? (
            <PDFViewerWithControls
              fileUrl={lastPresentationUrl}
              onPageChange={onPageChange}
              containerAspectRatio={MODERN_SLIDE_ASPECT_RATIO}
            />
          ) : (
            <div />
          ))
        }
      />

      <div className="w-full h-px bg-gray-200" />

      <div className="my-2">
        <div className="grid gap-2">
          <div className="grid grid-flow-col items-center gap-4">
            <ScenarioCallControls
              scenarioId={props.scenario.id}
              liveCallRenderProps={props.liveCallRenderProps}
              callStatus={props.callStatus}
              presentations={props.scenario.presentations ?? []}
              selectedPresentation={props.selectedPresentation}
              onPresentationChange={props.onPresentationChange}
              enableScreenSharing={props.enableScreenSharing}
              startScreenSharing={() => screenShare.start()}
              stopScreenSharing={() => screenShare.stop()}
              isScreenSharing={screenShare.active}
            />
          </div>
          {props.callStatus === CallStatus.ERROR && (
            <div className="text-xs text-red-700">
              {props.liveCallRenderProps.callState.error ?? "Unknown error"}
            </div>
          )}
          {props.callStatus === CallStatus.SAVE_FAILED && (
            <div className="text-xs">
              Don't worry, your call data is still here. We're just having
              trouble saving it.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
